import { PeekyEmitter } from "@shared/peeky.emitter";
import { getDimensions } from "@front/dom";
import { transitionend } from "@front/dom";
import { Peeky } from "./peeky";
import { throttle } from "@shared/throttle";

type Source = {
  src: string,
  type: string
}

export class PeekyVideo extends PeekyEmitter {

  DOM: HTMLVideoElement;
  originalSources: NodeListOf<HTMLSourceElement>;
  sources: HTMLSourceElement[];
  sourceList: Source[];
  pk: Peeky;
  data: any;
  poster: string;
  private _ready: boolean;
  private _paused: boolean;

  throttledResize: (...args: any) => void;
  throttledPreload: (...args: any) => void;;

  constructor(video: HTMLVideoElement, pk: Peeky) {
    super();

    let JSONdata: any;
    try {
      JSONdata = JSON.parse(video.dataset.video);
    } catch(e) {
      console.log('Could not parse video data: ');
      console.log(video.dataset.video);
      return;
    }

    this.DOM = video;
    this.originalSources = <NodeListOf<HTMLSourceElement>>video.querySelectorAll('source');
    this.pk = pk;
    this.poster = video.poster;
    this.data = JSONdata || {};
    this._ready = false;
    this._paused = false;

    this.throttledResize = throttle(this.onResize).bind(this);
    this.throttledPreload = throttle(this.onPreload).bind(this);

    this.one('videoLoaded', (e) => {
      this._ready = true;
    });

    video.poster = '';

    // Detach sources
    for (let i=0; i<this.originalSources.length; i++) {
      let source = this.originalSources[i];
      source.parentNode.removeChild(source);
    }
  }

  onResize() {
    this.load();
  };

  onPreload(e, data) {
    // Needs e.width && e.height
    if (data && data.width && data.height) {
      this.load({
        preload: true,
        dimensions: {
          width: data.width,
          height: data.height
        }
      });
    }
  }


  focusIn() {
    let video = this.DOM;
    this.load();
    video.classList.remove('notInFocus');
    this.pk.on('resize', this.throttledResize);
    this.pk.on('resizeImages', this.throttledResize);
    video.addEventListener('preloadImage', this.throttledPreload);

    if (video.autoplay && video.paused) {
      video.play();
    }
  }

  async focusOut() {
    let video = this.DOM;
    let sources = this.sources;
    if (sources) {
      this.sources = undefined;
      await transitionend(video);
      for (let i=0; i<sources.length; i++) {
        let source = sources[i];
        source.parentNode.removeChild(source);
      }
    }
    video.classList.add('notInFocus');
    this.pk.off('resize', this.throttledResize);
    this.pk.off('resizeImages', this.throttledResize);
    video.removeEventListener('preloadImage', this.throttledPreload);
  };


  load(options: { dimensions?: any, preload?: boolean } = {}) {
    let video = this.DOM;
    let sourceList = this.getVideoSource(options.dimensions) || [];
    let sources = [];

    if (this.sourceList && JSON.stringify(this.sourceList) === JSON.stringify(sourceList)) {
      return;
    }

    if (options.preload) {
      return;
    }

    for (let i=0; i<sourceList.length; i++) {
      let source = document.createElement('source');
      source.src = sourceList[i].src;
      source.type = sourceList[i].type;
      sources.push(source);
    }

    if (this.sources) {
      for (let i=0; i<this.sources.length; i++) {
        let source = this.sources[i];
        source.parentNode.removeChild(source);
      }
    }

    for (let i=0; i<sources.length; i++) {
      let source = sources[i];
      video.appendChild(source);
    }

    this.sources = sources;
    this.sourceList = sourceList;

    if (!this.ready()) {
      video.classList.add('load');
      video.classList.add('loading');
      video.dispatchEvent(new Event('pk-loading'));
      video.dispatchEvent(new Event('pk-video-loading'));
    }

    video.addEventListener('canplay', async (e) => {
      this.triggerAction('videoLoaded', { sourceList: sourceList });
      video.classList.remove('loading');
      video.dispatchEvent(new Event('pk-loaded'));
      video.dispatchEvent(new Event('pk-video-loaded'));
      await transitionend(video);
      video.classList.remove('load');
    });
  };

  getVideoSource(dimensions): Source[] {
    dimensions = dimensions ?? getDimensions(this.DOM);
    let dpr = Math.max((window.devicePixelRatio || 1) * 0.75, 1); // If hi-res screen, only use 75%
    let bestWidth = 0;
    let bestHeight = 0;
    let bestSource: Source[];
    let foundMatch = false;
    let containerWidth = dpr * dimensions.width;
    let containerHeight = dpr * dimensions.height;
    let aspectRatio = this.data.width / this.data.height;
    let style = window.getComputedStyle(this.DOM);
    let type = (style.objectFit === 'cover') ? 'cover' : 'contain';
    let versions: { [resolution: string]: Source[] } = this.data.versions || {};

    // Loop all versions of video
    for (let resolution in versions) {
      let thisSource = versions[resolution];
      let thisWidth: number;
      if (resolution === 'sd') {
        thisWidth = 640;
      } else if (resolution === 'hd') {
        thisWidth = 1280;
      } else if (resolution === 'fhd') {
        thisWidth = 1920;
      } else {
        thisWidth = parseFloat(resolution);
      }
      let thisHeight = thisWidth / aspectRatio;
      let test = false;
      
      if (type === 'cover') {
        // This matches the container
        if (thisWidth >= containerWidth && thisHeight >= containerHeight) {
          // This is smaller than previously found match
          if (!foundMatch || thisWidth*thisHeight < bestWidth*bestHeight) {
            foundMatch = true;
            test = true;
          }
        } else {
          // If previously found image also doesn't match, check if this is better
          if ((bestWidth < containerWidth || bestHeight < containerHeight) && (thisWidth*thisHeight > bestWidth*bestHeight)) {
            test = true;
          }
        }
      } else if (type === 'contain') {
        // This matches the container
        if (thisWidth >= containerWidth || thisHeight >= containerHeight) {
          // This is smaller than previously found match
          if (!foundMatch || thisWidth*thisHeight < bestWidth*bestHeight) {
            foundMatch = true;
            test = true;
          }
        } else {
          if (bestWidth < containerWidth && bestHeight < containerWidth && (thisWidth*thisHeight > bestWidth*bestHeight)) {
            test = true;
          }
        }
      }

      if (test || !bestSource) {
        bestWidth = thisWidth;
        bestHeight = thisHeight;
        bestSource = thisSource;
      }
    }

    return bestSource;
  }


  ready(callback?: () => any) {
    if (typeof(callback) === 'function') {
      if (this._ready) {
        callback();
      } else {
        this.on('ready', callback);
      }
    }
    return !!this._ready;
  }
}